// Thin fetch wrapper for session-gated /api/* endpoints.
//
// Responsibilities:
//   - always cross-origin credentialed (session cookie flow)
//   - auto-injects X-Organization-Id from the session store (when set)
//   - 401 → caller can bubble up; SessionGate + useSession will handle
//     the UI transition on next render
//
// The server re-validates X-Organization-Id against real memberships on
// every request (requireOrg). The header here is a CONVENIENCE, never
// the authority. A hostile client can set any value; it just won't help.

import { API_BASE_URL } from './config';
import { useSessionStore } from '../stores/session';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiOptions extends Omit<RequestInit, 'body'> {
  /** JSON-serializable request body. */
  body?: unknown;
  /**
   * Explicit org override. Normally omitted — the active org from the
   * store is used. Pass this only when a screen legitimately needs to
   * address a different org (rare; we mostly don't).
   */
  organizationId?: string;
  /** Skip auto-injection of X-Organization-Id (e.g. /api/me). */
  skipOrgHeader?: boolean;
}

export async function apiFetch<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const { body, organizationId, skipOrgHeader, headers, ...rest } = opts;

  const orgId = skipOrgHeader
    ? undefined
    : organizationId ?? useSessionStore.getState().activeOrganizationId;

  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...(orgId ? { 'X-Organization-Id': orgId } : {}),
    ...(headers as Record<string, string> | undefined),
  };

  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...rest,
    credentials: 'include',
    headers: finalHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  const parsed = text ? safeJson(text) : null;

  if (!res.ok) {
    const msg =
      (parsed && typeof parsed === 'object' && 'error' in parsed && typeof (parsed as { error: unknown }).error === 'string'
        ? (parsed as { error: string }).error
        : res.statusText) || 'Request failed';
    throw new ApiError(res.status, msg, parsed);
  }

  return parsed as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
